import { useTranslation } from '@frontend/i18n';
import { useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Button, CheckboxField, NakedButton, useFormField, useModalControl } from '@frontend/design-system';
import { COMPOSER_BREAKPOINTS } from '../../content-composer/constants';
import { ContentComposer } from '../../content-composer/content-composer';
import { OnSaveChanges, OnTemplateNameChange } from '../types';
import { ContinueButton } from './continue-button';
import { NameYourTemplateModal } from './name-your-template-modal';

type Props = {
  createTemplateAccess: boolean;
  disabledBack: boolean;
  editTemplateAccess: boolean;
  onBack: () => void;
  onSave: (data: OnSaveChanges, applyChanges: boolean) => void;
  onTemplateNameSave: (data: OnTemplateNameChange) => void;
  subject: string;
  templateTitle?: string;
  trackingPrefix: string;
};

export const EmailFooter = ({
  createTemplateAccess,
  disabledBack,
  editTemplateAccess,
  onBack,
  onSave,
  onTemplateNameSave,
  subject,
  templateTitle,
  trackingPrefix,
}: Props) => {
  const { t } = useTranslation('email-composer');
  const search = useSearch({ strict: false }) as { templateId?: string; isEdit?: boolean };
  const templateId = search.templateId;
  const isEdit = search.isEdit === true;
  const { modalProps, triggerProps } = useModalControl();
  const nameFieldProps = useFormField({ type: 'text', placeholder: t('New Template Title'), value: templateTitle }, [
    templateTitle,
  ]);
  const applyChangesFieldProps = useFormField({ type: 'checkbox', value: isEdit }, [isEdit]);

  return (
    <ContentComposer.Footer
      css={{
        columnGap: theme.spacing(3),
        gridTemplateColumns: 'auto 1fr',
        padding: theme.spacing(3, 5),
      }}
    >
      <div>
        <Button
          size='large'
          variant='tertiary'
          onClick={onBack}
          trackingId={`${trackingPrefix}-back-btn`}
          disabled={disabledBack}
        >
          {t('Back')}
        </Button>
      </div>
      <div
        css={{
          display: 'flex',
          gap: theme.spacing(2),
          justifyContent: 'flex-end',
        }}
      >
        {templateId && templateId !== 'new' && editTemplateAccess && (
          <NakedButton
            css={{
              alignItems: 'center',
              columnGap: theme.spacing(1),
              display: 'flex',
              marginRight: theme.spacing(2),
            }}
            trackingId={`${trackingPrefix}-apply-template-changes-btn`}
          >
            <CheckboxField {...applyChangesFieldProps} name='' label='' />
            <label
              htmlFor={applyChangesFieldProps.id}
              css={{
                cursor: 'pointer',
              }}
            >
              {t('Apply changes to template')}
            </label>
          </NakedButton>
        )}
        {createTemplateAccess && (
          <Button
            size='large'
            variant='secondary'
            {...triggerProps}
            css={{
              width: 'initial',
              [`@media (max-width: ${COMPOSER_BREAKPOINTS.MOBILE}px)`]: {
                display: 'none',
              },
            }}
            trackingId={`${trackingPrefix}-save-as-new-template-btn`}
          >
            {t('Save As New Template')}
          </Button>
        )}
        <ContinueButton
          onSave={(templateHtml, templateJson) =>
            onSave(
              { subject, templateHtml, templateId, templateJson, title: templateTitle ?? '' },
              applyChangesFieldProps.value
            )
          }
          openNewTemplateModal={triggerProps.onClick}
          trackingPrefix={trackingPrefix}
        />
      </div>
      {modalProps.show && (
        <NameYourTemplateModal
          fieldProps={nameFieldProps}
          modalProps={modalProps}
          onTemplateNameSave={(data) => {
            onTemplateNameSave(data);
            modalProps.onClose();
          }}
          subject={subject}
        />
      )}
    </ContentComposer.Footer>
  );
};
